import { UseMutationResult } from "@tanstack/react-query";
import { cloneDeep, isEqual } from "lodash";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { useContext, useEffect, useMemo, useState } from "react";
import { UpdateAddresses } from "../../../api/routes";
import * as client from "../../../client";
import { useToast } from "../../../context-providers/Toast";
import { ThemeContext } from "../../../themes/Theme";
import { SovColumnProps } from "./AddressCardColumns";
import {
  resolveConstructionCodeEdit,
  formatConstructionDisplay,
} from "./construction";
import styles from "./ColumnEditModal.module.css";
import {
  originalConstructionDescriptionFromRawAddress,
  originalOccupancyDescriptionFromRawAddress,
} from "./flags";
import { formatSicDisplay } from "./sic";
import { RawAddressWithIndices, SovGridContext } from "./types";
import { colorForConfidence } from "../dashboard/util";

type ColumnEditModalProps = {
  columnInfo: SovColumnProps | null;
  visible: boolean;
  fullAddresses: RawAddressWithIndices[];
  context: SovGridContext;
  onHide: () => void;
  setVisible: (visible: boolean) => void;
  // Better to do this or just mint it? I know this has come up in the past...
  updateAddresses: UseMutationResult<
    client.UpdateAddressResponse,
    Error,
    UpdateAddresses,
    unknown
  >;
  onSaved: () => void;
};

type GroupEntry = {
  label: string;
  origDesc?: string;
  confidence?: string;
  addresses: RawAddressWithIndices[];
};

const CONFIDENCE_PRIORITY = ["user edited", "low", "medium", "high"];

const getConfidence = (
  address: client.Address,
  field: keyof client.RawAddress
): string => {
  if (address.private_overrides[field] !== undefined) {
    return "user edited";
  }
  const flags = address.private_flags[field] ?? [];
  if (flags.includes("low_confidence")) {
    return "low";
  }
  if (flags.includes("medium_confidence")) {
    return "medium";
  }
  return "high";
};

const getInputGroupKey = (
  address: RawAddressWithIndices,
  field: keyof client.RawAddress
): string => {
  const fullAddr = address.__extras.fullAddress;
  const confidence = getConfidence(fullAddr, field);
  if (field === "construction_code") {
    const code = address.construction_code ?? "";
    const codeType = address.construction_code_type ?? "";
    const origDesc = originalConstructionDescriptionFromRawAddress(fullAddr);
    return `${code}\0${codeType}\0${origDesc}\0${confidence}`;
  }
  if (field === "sic_code") {
    const sicCode = address.sic_code ?? "";
    const occCode = address.occupancy_code ?? "";
    const occCodeType = address.occupancy_code_type ?? "";
    const origDesc = originalOccupancyDescriptionFromRawAddress(fullAddr);
    return `${sicCode}\0${occCode}\0${occCodeType}\0${origDesc}\0${confidence}`;
  }
  return "";
};

const buildInputGroupEntry = (
  addresses: RawAddressWithIndices[],
  field: keyof client.RawAddress
): GroupEntry | null => {
  const first = addresses[0];
  if (!first) return null;
  const fullAddr = first.__extras.fullAddress;
  const confidence = getConfidence(fullAddr, field);
  if (field === "construction_code") {
    return {
      label: formatConstructionDisplay(
        first.construction_code_type,
        first.construction_code
      ),
      origDesc:
        originalConstructionDescriptionFromRawAddress(fullAddr) || "NONE GIVEN",
      confidence,
      addresses,
    };
  }
  // sic_code
  return {
    label: formatSicDisplay(first.sic_code) || "No SIC code",
    origDesc:
      originalOccupancyDescriptionFromRawAddress(fullAddr) || "NONE GIVEN",
    confidence,
    addresses,
  };
};

export const ColumnEditModal: React.FC<ColumnEditModalProps> = ({
  columnInfo,
  visible,
  fullAddresses,
  context,
  onHide,
  setVisible,
  updateAddresses,
  onSaved,
}) => {
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [newValue, setNewValue] = useState<string | number>("");
  const [groupByParsedInputs, setGroupByParsedInputs] = useState(false);
  const toast = useToast();
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  useEffect(() => {
    if (!visible) {
      setSelectedIndices([]);
      setNewValue("");
      setGroupByParsedInputs(false);
    }
  }, [visible]);

  const workingField = columnInfo?.field;

  const supportsInputGrouping =
    workingField === "construction_code" || workingField === "sic_code";

  const groupEntries: GroupEntry[] = useMemo(() => {
    if (!columnInfo || !workingField) return [];
    if (groupByParsedInputs && supportsInputGrouping) {
      const grouped = Map.groupBy(fullAddresses, (address) =>
        getInputGroupKey(address, workingField)
      );
      return Array.from(grouped.values())
        .map((addresses) => buildInputGroupEntry(addresses, workingField))
        .filter((entry): entry is GroupEntry => entry !== null)
        .sort((a, b) => {
          const confidenceDiff =
            CONFIDENCE_PRIORITY.indexOf(a.confidence ?? "high") -
            CONFIDENCE_PRIORITY.indexOf(b.confidence ?? "high");
          if (confidenceDiff !== 0) return confidenceDiff;
          return b.addresses.length - a.addresses.length;
        });
    }
    const grouped = Map.groupBy(
      fullAddresses,
      (address) => columnInfo.getValue(address, context) || "Blank"
    );
    return Array.from(grouped, ([label, addresses]) => ({
      label: String(label),
      addresses,
    })).sort((a, b) => b.addresses.length - a.addresses.length);
  }, [
    fullAddresses,
    groupByParsedInputs,
    supportsInputGrouping,
    workingField,
    columnInfo,
    context,
  ]);

  if (!columnInfo || !workingField) {
    return <></>;
  }

  // Calculate total addresses to update based on selected groups
  const totalAddressesToUpdate = selectedIndices.reduce<number>(
    (sum, idx) => sum + (groupEntries[idx]?.addresses.length ?? 0),
    0
  );

  const addressesToUpdate = async (): Promise<client.Address[]> => {
    const selectedAddresses = selectedIndices.flatMap(
      (idx) => groupEntries[idx]?.addresses ?? []
    );

    return selectedAddresses.map((fullAddress) => {
      const addressToUpdate = cloneDeep(fullAddress.__extras.fullAddress);
      if (
        isEqual(newValue, addressToUpdate.private_computed_values[workingField])
      ) {
        delete addressToUpdate.private_overrides[workingField];
      } else {
        addressToUpdate.private_overrides[workingField] = newValue;
      }
      // When editing construction_code, also update code_type and description
      if (workingField === "construction_code" && newValue) {
        const { codeType, description } = resolveConstructionCodeEdit(
          String(newValue)
        );
        addressToUpdate.private_overrides.construction_code_type = codeType;
        addressToUpdate.private_overrides.construction_description =
          description;
      }
      return addressToUpdate;
    });
  };

  const handleSave = async () => {
    try {
      const addresses = await addressesToUpdate();
      await updateAddresses.mutateAsync({ addresses: addresses });
      onSaved();
    } catch (err) {
      toast.show({
        severity: "error",
        summary: "Failed to update addresses",
        sticky: true,
        icon: "pi pi-times",
      });
    }
  };

  // Detect if all addresses are US
  const isUnitedStates = fullAddresses.every(
    (addr) => addr.country_code === "US"
  );

  // Use modalEditor if available
  let editorNode: React.ReactNode = null;
  if (columnInfo.editor) {
    const ColumnModalEditor = columnInfo.editor;
    // Only pass countryCode if the editor supports it
    editorNode = (
      <ColumnModalEditor
        options={{ value: newValue, editorCallback: setNewValue }}
        disabled={updateAddresses.isPending}
        isUnitedStates={isUnitedStates}
        fieldType={columnInfo.field === "tiv" ? "number" : "string"}
        additionalStyles={{}}
      />
    );
  }

  return (
    <Dialog
      header={`Bulk Edit ${columnInfo.columnName}`}
      className={styles.dialog}
      visible={visible}
      onHide={() => setVisible(false)}
      footer={
        <div className={styles.dialogFooter}>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={onHide}
            severity="secondary"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            onClick={handleSave}
            disabled={totalAddressesToUpdate === 0}
            loading={updateAddresses.isPending}
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        <div className={styles.pairedLabelAndBox}>
          <label htmlFor="bulk-multiselect" className={styles.multiSelectLabel}>
            <strong>Search</strong>
          </label>
          {supportsInputGrouping && (
            <div className={styles.toggleRow}>
              <InputSwitch
                checked={groupByParsedInputs}
                onChange={(e) => {
                  setGroupByParsedInputs(e.value);
                  setSelectedIndices([]);
                }}
              />
              <span className={styles.toggleLabel}>
                Group by original inputs
              </span>
            </div>
          )}
          <MultiSelect
            value={selectedIndices}
            scrollHeight="400px"
            onChange={(e) => setSelectedIndices(e.value)}
            options={groupEntries.map((entry, idx) => ({
              idx,
              label: entry.label,
              origDesc: entry.origDesc,
              confidence: entry.confidence,
              count: entry.addresses.length,
            }))}
            className={styles.multiSelect}
            panelClassName={styles.multiSelectPanel}
            maxSelectedLabels={4}
            filter
            filterBy="label"
            placeholder="Matching values to update"
            optionLabel="label"
            optionValue="idx"
            display="chip"
            inputId="bulk-multiselect"
            itemTemplate={(item) => {
              if (item.confidence) {
                const bgColor = colorForConfidence(
                  item.confidence,
                  isLightMode
                );
                const capitalizedConfidence =
                  item.confidence.charAt(0).toUpperCase() +
                  item.confidence.slice(1);
                return (
                  <div>
                    <div className={styles.multiSelectItemTemplateName}>
                      {item.label}
                    </div>
                    <div className={styles.multiSelectItemTemplateSummary}>
                      {item.origDesc}
                      <span
                        className={styles.confidenceBadge}
                        style={bgColor ? { backgroundColor: bgColor } : {}}
                      >
                        {capitalizedConfidence}
                      </span>
                      <span> — {item.count} addresses</span>
                    </div>
                  </div>
                );
              }
              return (
                <div>
                  <div className={styles.multiSelectItemTemplateName}>
                    {item.label}
                  </div>
                  <div className={styles.multiSelectItemTemplateSummary}>
                    {item.count} addresses impacted
                  </div>
                </div>
              );
            }}
          />
        </div>
        <div className={styles.pairedLabelAndBox}>
          <label htmlFor="bulk-replace" className={styles.multiSelectLabel}>
            <strong>Replace with</strong>
          </label>
          {editorNode}
        </div>
        {totalAddressesToUpdate > 0 && (
          <div>
            <Message
              // Consider changing to info severity if the number of addresses is small
              severity="warn"
              text={`This will update ${totalAddressesToUpdate} addresses`}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};
